// Pull a one-paragraph hover-card preview out of a wiki page's
// markdown body. Intentionally crude — we want a quick "what is this
// page about" excerpt for citation hover-cards, not a faithful
// markdown render. The extractor:
//
//   1. Splits the body on blank lines (paragraph boundaries)
//   2. Skips blocks that lead with a heading marker (`#`), a wiki-style
//      HTML comment (`<!--`), or a horizontal rule (`---`)
//   3. Picks the first remaining block as the snippet candidate
//   4. Strips a leading list / blockquote marker (`-`, `*`, `>`, `1.`)
//      so the snippet reads as prose
//   5. Collapses internal whitespace + newlines to single spaces
//   6. Truncates past `maxChars` with an ellipsis
//
// Pure function — testable without Vue or Pinia. Used by the
// wiki_pages store to populate its hover-card snippet cache.

const DEFAULT_MAX_CHARS = 200

export function extractSnippet(content: string, maxChars: number = DEFAULT_MAX_CHARS): string {
  if (!content) return ''
  const blocks = content.split(/\n\s*\n/)
  for (const block of blocks) {
    const trimmed = block.trim()
    if (!trimmed) continue
    if (isSkippable(trimmed)) continue
    const cleaned = stripLineMarkers(trimmed).replaceAll(/\s+/g, ' ').trim()
    if (!cleaned) continue
    if (cleaned.length <= maxChars) return cleaned
    return `${cleaned.slice(0, maxChars - 1).trimEnd()}…`
  }
  return ''
}

function isSkippable(block: string): boolean {
  const firstChar = block[0]
  if (firstChar === '#') return true // heading
  if (block.startsWith('<!--')) return true // markdown HTML comment
  if (/^-{3,}\s*$/.test(block)) return true // horizontal rule
  if (block.startsWith('```')) return true // fenced code block
  return false
}

// Strip line-leading list / blockquote markers from EVERY line in the
// block (not just the first), so a multi-line bullet list reads as
// joined prose after the whitespace collapse — `- a\n- b\n- c` becomes
// `a b c`, not `a - b - c`.
function stripLineMarkers(block: string): string {
  return block
    .split('\n')
    .map((line) =>
      line
        .replace(/^[>\s]+/, '')
        .replace(/^[-*+]\s+/, '')
        .replace(/^\d+\.\s+/, ''),
    )
    .join('\n')
}
